import { productEvent } from "@shared/analytics/productEvents";
import { ArrowShortUpRightIcon } from "@solomon-labs/design/components/icons";
import { ButtonIcon, Copy, Text } from "@solomon-labs/design/components/ui";
import React from "react";

interface SecurityAddressProps {
    /** The full base58 address. Shown shortened when space runs out, always
     *  copied in full. */
    address: string;
    /** What the address is, as read mid-sentence: "mint authority", "USDv mint". */
    name: string;
    /** Where to inspect it. For a token control this is the mint, not the
     *  authority account. */
    explorerHref: string;
    /** Spoken after "Solscan", so the action's accessible name says what opens. */
    explorerContext: string;
}

export function SecurityAddress({
    address,
    name,
    explorerHref,
    explorerContext
}: SecurityAddressProps): React.ReactElement {
    return (
        <div className="flex min-w-0 items-center gap-1">
            <Copy
                content={address}
                onClick={() =>
                    productEvent("stats:copy_requested", {
                        entry_point: "stats_page",
                        section: "security",
                        control: name
                    })
                }
                copyLabel={`Copy ${name} address`}
                copiedLabel={`${name} address copied`}
                className="min-w-0 gap-2"
            >
                <span className="min-w-0" title={address}>
                    <Text
                        variant="small"
                        render={<span />}
                        className="block truncate font-numeric-mono"
                        aria-hidden
                    >
                        {address}
                    </Text>
                    <span className="sr-only">{address}</span>
                </span>
            </Copy>

            <ButtonIcon
                variant="transparent"
                size="lg"
                icon={<ArrowShortUpRightIcon aria-hidden="true" />}
                aria-label={`Solscan, ${explorerContext}, opens in a new tab`}
                title={`Open ${explorerContext} on Solscan`}
                className="size-7 shrink-0"
                onClick={() => {
                    productEvent("stats:link_clicked", {
                        entry_point: "stats_page",
                        section: "security",
                        link_type: "address_explorer",
                        control: name
                    });
                    window.open(explorerHref, "_blank", "noopener,noreferrer");
                }}
            />
        </div>
    );
}
